import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { useCookies } from 'react-cookie';
import { useNavigate, useParams } from 'react-router-dom';
import { arrayMoveImmutable } from 'array-move';
import { MenuOutlined } from '@ant-design/icons';
import {
  sortableContainer,
  sortableElement,
  sortableHandle,
} from 'react-sortable-hoc';
import 'utils/style/sortDragging.css';
import { cashingApis } from 'apis/common/cashingApis';
import { oldNoticeApi, noticeApisLIVE } from 'apis/operation/oldNoticeApis';
import { noticeTypeList, PublicState as PublicStateList } from 'utils/constant';
import DefaultLayout from 'components/layout/DefaultLayout';
import MainTable from 'components/table/MainTable';
import CapsuleButton from 'components/button/CapsuleButton';
import DeletePopConfirm from 'components/button/DeletePopConfirm';
import TableToolbar from 'components/table/TableToolbar';
import SearchInput from 'components/search/SearchInput';
import PublicState from 'components/etc/PublicState';
import UtcTimer from 'components/etc/UtcTimer';
import TextButton from 'components/button/TextButton';
import DefaultButton from 'components/button/DefaultButton';
import {
  useNoticeDelete,
  useNoticeList,
} from 'hooks/queries/operation/noticeQuery';
import { useTranslation } from 'react-i18next';
/**
 * * 운영 관리 & 작품(오토메) - 공지사항 목록
 */

// 정렬 아이콘
const DragHandle = sortableHandle(() => (
  <MenuOutlined style={{ cursor: 'pointer', color: '#999' }} />
));
const SortableItem = sortableElement((props) => <tr {...props} />);
const SortableContainer = sortableContainer((props) => <tbody {...props} />);

const NoticeListPage = () => {
  const publicState = PublicStateList();
  const { t } = useTranslation();
  const { projectId } = useParams();
  const [cookies] = useCookies();
  const navigate = useNavigate();
  // const authUpdate = JSON.parse(cookies?.managementAuth);
  const [searchData, setSearchData] = useState();
  const [deployModalOpen, setDeployModalOpen] = useState(false); // 배포 모달 visible

  const { data: noticeList } = useNoticeList(projectId);
  const { mutate: noticeDelete } = useNoticeDelete();

  /// 모달 열기
  const handleOpen = () => {
    setDeployModalOpen(true);
  };

  const handleLink = (record) => () => {
    navigate(`${record.notice_no}/update`, {
      state: { record: record },
    });
  };

  /// 정렬 드래깅이 끝날 때 => api 호출
  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(
        [].concat(noticeList),
        oldIndex,
        newIndex,
      ).filter((el) => !!el);

      const sortData = newData.map((v, i) => {
        v.sortkey = i;
        return v;
      });

      const res =
        cookies.server === 'test'
          ? await oldNoticeApi.sorting(sortData)
          : await noticeApisLIVE.sorting_LIVE(sortData);

      if (!res) {
        return false;
      }
      // 캐싱 비우기
      await cashingApis.notice_promotion_cash();
      message.success(t('notice.table.noticePromotionCashMessage'));
    }
  };

  const DraggableContainer = (props) => {
    return (
      <SortableContainer
        useDragHandle
        disableAutoscroll
        helperClass='row-dragging' // 정렬 아이콘 클릭 된 상태로 움직일 때의 모습 스타일링
        onSortEnd={onSortEnd}
        {...props}
      />
    );
  };

  const DraggableBodyRow = ({ className, style, ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index

    // ***** int로 변환해주었어야 했다!
    const index = noticeList?.findIndex(
      (item) => parseInt(item.key) === parseInt(restProps['data-row-key']),
    );
    return <SortableItem index={index} {...restProps} />;
  };

  /// 삭제하기
  const handleDelete = (record) => () => {
    let noticeId = record.notice_no;
    noticeDelete({ noticeId });
  };

  const columnsOrigin = [
    {
      title: t('notice.table.type'),
      dataIndex: 'notice_type',
      width: '5rem',
      filters: noticeTypeList,
      onFilter: (value, record) => record.notice_type.indexOf(value) === 0,
      render: (text) => noticeTypeList?.map((v) => v.value === text && v.text),
    },
    {
      title: t('notice.table.internalName'),
      dataIndex: 'notice_name',
      width: '15rem',
      sorter: (a, b) => a.notice_name.localeCompare(b.notice_name),
      render: (text, record) => (
        <TextButton onClick={handleLink(record)}>{text}</TextButton>
      ),
    },
    {
      title: t('notice.table.publicStatus'),
      dataIndex: 'is_public',
      width: '5rem',
      render: (text) => <PublicState value={text} />,
      filters: publicState,
      onFilter: (value, record) => record?.is_public === value,
      sorter: (a, b) => a.is_public - b.is_public,
    },
    {
      title: t('notice.table.startDate'),
      dataIndex: 'start_date',
      width: '10rem',
      render: (text) => (
        <>
          <p>{text.slice(0, -3)}</p>
          <UtcTimer startDate={text} />
        </>
      ),
      sorter: (a, b) => a.start_date.localeCompare(b.start_date),
    },
    {
      title: t('notice.table.endDate'),
      dataIndex: 'end_date',
      width: '10rem',
      render: (text) => (
        <>
          <p>{text.slice(0, -3)}</p>
          <UtcTimer endDate={text} />
        </>
      ),
      sorter: (a, b) => a.end_date.localeCompare(b.end_date),
    },
    {
      title: t('notice.table.action'),
      dataIndex: 'move',
      width: '10rem',
      render: (text, record) => (
        <div>
          <CapsuleButton name='update' onClick={handleLink(record)}>
            {t('notice.table.edit')}
          </CapsuleButton>
          <DeletePopConfirm
            title={t('notice.table.deleteMessage')}
            onConfirm={handleDelete(record)}
            disabled={authUpdate}
          />
        </div>
      ),
    },
    {
      title: t('notice.table.sort'),
      width: '3rem',
      render: () => <DragHandle disabled={authUpdate} />,
    },
  ];

  // useEffect(() => {
  //   '';
  // }, [cookies.server, cookies.lang]);

  return (
    <>
      <TableToolbar>
        <SearchInput
          placeholder={t('notice.header.inputPlaceHolder')}
          setSearchData={setSearchData}
        />
        <div>
          <DefaultButton
            name='register'
            onClick={handleLink({ notice_no: -1 })}
            disabled={authUpdate}
          >
            {t('notice.header.registerNotice')}
          </DefaultButton>
          <>
            {projectType !== 'otome' && (
              <DefaultButton
                name='deploy'
                onClick={handleOpen}
                disabled={authUpdate}
                typename='sub'
              >
                {t('notice.header.liveDeployment')}
              </DefaultButton>
            )}
          </>
        </div>
      </TableToolbar>
      <MainTable
        columns={columnsOrigin}
        dataSource={
          searchData
            ? noticeList?.filter(
                (data) => data?.notice_name?.indexOf(searchData) !== -1,
              )
            : noticeList
        }
        rowKey={(record) => `${record.key}`}
        rowClassName={(record) => record.episode_type === 'side' && 'red'}
        components={
          noticeList?.length > 0 && {
            body: {
              style: { color: 'blue' },
              wrapper: DraggableContainer,
              row: DraggableBodyRow,
            },
          }
        }
      />
      {/*<DeployModal*/}
      {/*  title={t('notice.deployModal.title')}*/}
      {/*  kind="notice"*/}
      {/*  visible={deployModalOpen}*/}
      {/*  setVisible={setDeployModalOpen}*/}
      {/*/>*/}
    </>
  );
};

export default NoticeListPage;
